import type { ReactNode } from "react";
import ReactDOM from "react-dom/client";
import { hasIndexedDB } from "@client/offline/hasIndexedDB";
import {
  initializePostHog,
  releaseInitialPageview,
} from "@client/telemetry/posthog";
import { isE2EPostHogProbe } from "@client/telemetry/posthogProbeSeam";
import { initializeSentry } from "@client/telemetry/sentry";
import { buildSentryInitOptions } from "@client/telemetry/sentryOptions";
import {
  reportAppLaunch,
  reportStorageBackend,
  type StorageBackend,
} from "@client/telemetry/storageBackend";
import { TRPCProvider } from "@client/TRPCProvider";
import {
  claimWarmImportFailure,
  isWarmImportInFlight,
  warmRegisteredChunksWhenIdle,
} from "@client/utils/backgroundChunkWarming";
import { installWorkspaceFavicon } from "@client/utils/workspaceFavicon";
import { installDevConsoleCapture } from "@client/utils/devDebug";
import {
  isDeployedEnvironment,
  isProductAnalyticsEnvironment,
} from "@client/utils/environment";
import { startSessionRecordingWhenStartupSettled } from "@client/utils/posthogRecording";

const vitePreloadReloadParam = "sProcessVitePreloadReloadAt";
const vitePreloadReloadCooldownMs = 60_000;

/**
 * Boots a web HTML entry — S-Process, Spec Grants and Ops each call this from
 * their `main.tsx` — so every entry recovers from a deploy's stale chunks,
 * survives Lockdown Mode's missing IndexedDB and reports to the same
 * telemetry. The native shell has its own entry (main-mobile.tsx).
 */
export function bootWebEntry(app: ReactNode) {
  installDevConsoleCapture();
  installWorkspaceFavicon();
  installVitePreloadErrorHandler();

  const storageReady = prepareStorage();
  const postHogReady = initializeWebPostHog(storageReady);

  initializeWebSentry(postHogReady, storageReady);

  void storageReady.then(() => {
    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
      <TRPCProvider>{app}</TRPCProvider>,
    );
  });

  // After startup settles (load + grace period + idle — see
  // runWhenStartupSettled), fetch every route-lazy chunk in the background
  // (one per idle window) so the complete app is cached from the very first
  // visit — route splitting trades startup parse for on-demand loads, and this
  // buys back offline coverage and post-deploy chunk consistency without
  // putting the chunks back on the critical path.
  warmRegisteredChunksWhenIdle();
}

function getVitePreloadReloadAt(): number {
  try {
    return Number(
      new URL(window.location.href).searchParams.get(vitePreloadReloadParam) ??
        "0",
    );
  } catch {
    return 0;
  }
}

function reloadWithVitePreloadMarker(reloadAt: number) {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(vitePreloadReloadParam, String(reloadAt));
    window.location.replace(url);
    return;
  } catch {
    window.location.reload();
  }
}

function clearVitePreloadReloadMarker() {
  window.setTimeout(() => {
    try {
      const url = new URL(window.location.href);

      if (!url.searchParams.has(vitePreloadReloadParam)) {
        return;
      }

      url.searchParams.delete(vitePreloadReloadParam);
      window.history.replaceState(window.history.state, "", url);
    } catch {
      // Leaving the marker in the URL is harmless if cleanup is blocked.
    }
  }, vitePreloadReloadCooldownMs);
}

// vite:preloadError is a browser event that Vite emits when it fails to load a
// dynamically imported chunk.
//
// If a user has an old app bundle open during or after a deploy, that old
// bundle may later try to import an old chunk URL that no longer exists. Vite
// catches that failed dynamic import and dispatches vite:preloadError.
//
// Calling event.preventDefault() tells Vite/browser handling: “we handled this
// failure ourselves.” In our case, the right recovery is usually a reload,
// because the fresh HTML points at the current chunk filenames.
function installVitePreloadErrorHandler() {
  clearVitePreloadReloadMarker();

  const reloadUnlessInCooldown = () => {
    const lastReloadAt = getVitePreloadReloadAt();

    if (
      Number.isFinite(lastReloadAt) &&
      Date.now() - lastReloadAt < vitePreloadReloadCooldownMs
    ) {
      return;
    }

    const reloadAt = Date.now();
    reloadWithVitePreloadMarker(reloadAt);
  };

  window.addEventListener("vite:preloadError", (event) => {
    // Every branch below recovers on its own terms, so Vite's default throw
    // is always suppressed. NOTE the side effect the warm handshake depends
    // on: preventDefault makes Vite's preload helper resolve (undefined)
    // instead of rethrowing.
    event.preventDefault();

    if (!isWarmImportInFlight()) {
      // No background chunk warm in flight, so this is a user navigation's
      // failure: take the reload path immediately, as before the warm
      // existed.
      reloadUnlessInCooldown();
      return;
    }

    // Ambiguous window: the failure may be the warm's own or a concurrent
    // user navigation's — both run the same imports, and at dispatch time
    // the owner is unknowable. Defer one macrotask: by then the warm's
    // promise has settled and recorded its failure, if it was the one that
    // failed. A warm failure is never answered with a page reload out from
    // under the user (recovery is the out-of-date banner or the user's own
    // navigation, which re-imports); anything unclaimed is a user
    // navigation's failure and reloads. Claims are one-per-record, so a
    // warm failure and a user failure in the same window still reload
    // exactly once.
    window.setTimeout(() => {
      if (claimWarmImportFailure()) {
        return;
      }

      reloadUnlessInCooldown();
    }, 0);
  });
}

// Safari's Lockdown Mode strips IndexedDB from the page; without a backing,
// the first Dexie open crashes the load (see memoryStandInStorage.ts). The
// stand-in is imported dynamically so the ordinary boot never downloads it;
// render waits for the install, which for everyone else is a resolved
// promise. If the chunk itself fails to load, render anyway — the app is no
// worse off than without the fallback.
async function prepareStorage(): Promise<StorageBackend> {
  const backend = await installStorageBackend();

  reportStorageBackend(backend);
  // The web deploy is identified by the release id the Sentry build plugin
  // injects (the git sha); the same value Sentry shows as `release`.
  reportAppLaunch({
    appBuild: (globalThis as { SENTRY_RELEASE?: { id?: string } })
      .SENTRY_RELEASE?.id,
  });
  // Boot telemetry is registered; the initial pageview may go out now, and
  // it carries every property above regardless of which chunk loaded first.
  releaseInitialPageview();
  // Console too: tags never reach the dev console or Debug panel, and a raw
  // storage error there is ambiguous without this one line.
  console.info(`[storage] backend: ${backend}`);

  return backend;
}

async function installStorageBackend(): Promise<StorageBackend> {
  if (hasIndexedDB()) {
    return "indexeddb";
  }

  try {
    const m = await import("@client/offline/memoryStandInStorage");
    return m.installMemoryStandInStorage() ? "memory-standin" : "indexeddb";
  } catch {
    // The fallback chunk failed to load: no IndexedDB and no stand-in either.
    return "unavailable";
  }
}

// Only deployments report telemetry — see isDeployedEnvironment. A production
// *build* isn't one: the e2e prod-build harness and a local
// `bun run build && bun run start` both serve one from 127.0.0.1, carrying the
// compiled-in Sentry DSN and (on a developer machine, from `.env`) the PostHog
// key, so their fixture-data events used to land in the shared production
// projects as if a funder had generated them.
function initializeWebPostHog(
  storageReady: Promise<StorageBackend>,
): Promise<void> {
  const posthogKey: string | undefined = import.meta.env
    .VITE_PUBLIC_POSTHOG_KEY;

  if (!posthogKey) {
    return Promise.resolve();
  }

  // The e2e probe (see telemetry/posthogProbeSeam.ts) initializes PostHog
  // against this origin so a test can watch the SDK's real requests. It is
  // asked only when a key is baked in, so a keyless build still drops the SDK
  // entirely, as it always has (the e2e harness bakes a placeholder key).
  const postHogProbe = isE2EPostHogProbe();

  if (!isDeployedEnvironment() && !postHogProbe) {
    return Promise.resolve();
  }

  // Previews initialize PostHog only so feature flags can evaluate; their
  // clicks are fixture data and must stay out of the shared analytics. See
  // isProductAnalyticsEnvironment.
  const capturesProductAnalytics =
    postHogProbe || isProductAnalyticsEnvironment();

  // PostHog initializes only once the boot telemetry above has been queued:
  // the SDK's own emitters (page-leave, web vitals, the deferred initial
  // pageview) then all run after the registrations are flushed, so no event
  // can go out without the session's storage backend and build. On a normal
  // device that wait is a microtask; under Lockdown it is the stand-in chunk.
  const postHogReady = storageReady.then(() =>
    initializePostHog(posthogKey, {
      api_host: postHogProbe
        ? `${window.location.origin}/ph`
        : "https://s-process.app/ph",
      // The probe runs under automation, which the SDK otherwise treats as
      // a bot and silently drops every event for.
      ...(postHogProbe ? { opt_out_useragent_filter: true } : {}),
      defaults: "2025-11-30",
      ui_host: "https://us.posthog.com",
      // Flags are targeted to the authenticated user's identity/properties, so
      // wait for identify() instead of fetching once for the anonymous ID first.
      advanced_disable_feature_flags_on_first_load: true,
      // Keep the session recorder off the critical load path; started once
      // startup settles (load + grace period + idle).
      disable_session_recording: true,
      // We don't use PostHog surveys. Left to its default, the SDK fetches its
      // remote surveys script (~32KB) the moment the remote config response
      // lands — traced at ~870ms into a funder review load, the middle of the
      // media window — for a feature nothing here renders.
      disable_surveys: true,
      autocapture: capturesProductAnalytics,
      capture_pageview: capturesProductAnalytics,
      capture_pageleave: capturesProductAnalytics,
      capture_heatmaps: capturesProductAnalytics,
      capture_dead_clicks: capturesProductAnalytics,
      // Real-user load timings (LCP/FCP/INP/CLS), so we can see what a funder
      // far from the Ohio server actually experiences instead of inferring it
      // from CI benchmarks that run against localhost. `network_timing` stays
      // off: it only feeds session replay and would ship a timing entry per
      // request (volume, plus request URLs) for no added insight here.
      capture_performance: capturesProductAnalytics
        ? { web_vitals: true, network_timing: false }
        : false,
    }),
  );

  if (capturesProductAnalytics) {
    startSessionRecordingWhenStartupSettled();
  }

  return postHogReady;
}

// `PROD` is the build-mode gate that lets Vite strip this branch from dev
// bundles; `isDeployedEnvironment` is the runtime one — a locally served
// production build must not report. Config is shared with the native entry —
// see telemetry/sentryOptions.ts.
function initializeWebSentry(
  postHogReady: Promise<void>,
  storageReady: Promise<StorageBackend>,
) {
  if (!import.meta.env.PROD || !isDeployedEnvironment()) {
    return;
  }

  void initializeSentry(async (Sentry) => {
    // Preserve the existing initialization order: PostHog must be ready before
    // Sentry replays an early error so beforeSend can attach its replay link.
    await postHogReady;
    // Same for the storage_backend tag: it must be on the global scope before
    // connect replays buffered startup errors — a Lockdown session crashing at
    // boot is exactly the event the tag exists to diagnose. Never hangs: every
    // path through storageReady settles, including the chunk-load failure.
    await storageReady;
    Sentry.init(buildSentryInitOptions(Sentry));
  });
}
